import type { PlotMedia, PlottingContext } from "@pdfking/renderer-api"

import type { Guide, LazyPage, RequestMedia } from "#public"

import { PlotGroup } from "./PlotGroup"

// The page-stack orientation layer: lays the groups out in a column ("normal")
// or a row ("stripe") and owns everything that flips between the two — the
// flex direction, the `w-max` overflow escape a row needs, and where the
// centring padding lands (leading/trailing along the scroll axis, cross-axis
// fill so `items-center` centres the single strip). `zoom` fits the whole
// stack; the group ids let the viewer scroll/detect by group. Purely
// presentational — the viewer measures `center_padding`/`zoom` and hands them
// down.
export function PlotStack({
  active_guides,
  allow_selection,
  bleed,
  center_padding,
  context,
  group_starts,
  groups,
  media_count,
  on_page_clicked,
  printable,
  request_media,
  stripe,
  uuid,
  zoom,
}: {
  active_guides: Guide[]
  allow_selection: boolean
  bleed: boolean
  center_padding: { cross: number; lead: number; trail: number }
  context: PlottingContext<unknown>
  group_starts: number[]
  groups: LazyPage[][]
  media_count: number
  on_page_clicked: ((info: { number: number; page: PlotMedia }) => void) | undefined
  printable: boolean
  request_media: RequestMedia | undefined
  stripe: boolean
  uuid: string
  zoom: number
}) {
  return (
    <div
      // In row mode a plain block box would still claim only the scroll
      // container's width — `w-max` lets it grow past the viewport so the
      // whole stripe can overflow and scroll horizontally instead of every
      // group flex-shrinking to 0. `center_padding` frames the groups so the
      // first/last can reach the viewport centre and the strip is centred on
      // its cross axis.
      className={`flex items-center ${stripe ? "w-max flex-row" : "flex-col"}`}
      style={
        stripe
          ? {
              minHeight: `${center_padding.cross}px`,
              paddingLeft: `${center_padding.lead}px`,
              paddingRight: `${center_padding.trail}px`,
              zoom,
            }
          : {
              minWidth: `${center_padding.cross}px`,
              paddingBottom: `${center_padding.trail}px`,
              paddingTop: `${center_padding.lead}px`,
              zoom,
            }
      }
    >
      {groups.map((group, gi) => (
        <PlotGroup
          active_guides={active_guides}
          allow_selection={allow_selection}
          bleed={bleed}
          context={context}
          id={`${uuid}-g-${gi}`}
          key={gi}
          media_count={media_count}
          on_page_clicked={on_page_clicked}
          pages={group}
          printable={printable}
          request_media={request_media}
          start={group_starts[gi]}
          uuid={uuid}
        />
      ))}
    </div>
  )
}
